import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { INDUSTRIES } from "../lib/quiz";
import { ArrowRight } from "lucide-react";

const initial = {
    first_name: "",
    last_name: "",
    email: "",
    organization: "",
    website: "",
    industry: "",
    phone: "",
    city_state: "",
    requested_report: false,
};

export default function LeadForm() {
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();

    const setField = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const validate = () => {
        const e = {};
        if (!form.first_name.trim()) e.first_name = "Required";
        if (!form.last_name.trim()) e.last_name = "Required";
        if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Valid email required";
        if (!form.organization.trim()) e.organization = "Required";
        if (!form.industry) e.industry = "Required";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        sessionStorage.setItem("ash_lead", JSON.stringify(form));
        navigate("/quiz");
    };

    return (
        <Layout>
            <section className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 fade-in" data-testid="lead-form-section">
                <div className="text-xs uppercase tracking-widest font-bold text-ash-orange mb-4">
                    Step 1 of 2
                </div>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-ash-black">
                    First, tell us a bit about you.
                </h1>
                <p className="mt-4 text-ash-gray-dark text-lg">
                    Your answers help us generate more personalized recommendations.
                </p>

                <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="lead-form">
                    <Field label="First name" required error={errors.first_name}>
                        <input data-testid="field-first-name" className="field-input" value={form.first_name} onChange={setField("first_name")} />
                    </Field>
                    <Field label="Last name" required error={errors.last_name}>
                        <input data-testid="field-last-name" className="field-input" value={form.last_name} onChange={setField("last_name")} />
                    </Field>
                    <Field label="Email" required error={errors.email} className="md:col-span-2">
                        <input data-testid="field-email" type="email" className="field-input" value={form.email} onChange={setField("email")} />
                    </Field>
                    <Field label="Organization / Business name" required error={errors.organization} className="md:col-span-2">
                        <input data-testid="field-organization" className="field-input" value={form.organization} onChange={setField("organization")} />
                    </Field>
                    <Field label="Website URL (if applicable)" className="md:col-span-2">
                        <input data-testid="field-website" className="field-input" value={form.website} onChange={setField("website")} placeholder="https://" />
                    </Field>
                    <Field label="Industry / Organization type" required error={errors.industry} className="md:col-span-2">
                        <select data-testid="field-industry" className="field-input" value={form.industry} onChange={setField("industry")}>
                            <option value="">Select one…</option>
                            {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                        </select>
                    </Field>
                    <Field label="Phone number (optional)">
                        <input data-testid="field-phone" className="field-input" value={form.phone} onChange={setField("phone")} />
                    </Field>
                    <Field label="City / State (optional)">
                        <input data-testid="field-city-state" className="field-input" value={form.city_state} onChange={setField("city_state")} />
                    </Field>

                    <label className="md:col-span-2 flex items-start gap-3 mt-2 cursor-pointer" data-testid="field-requested-report-label">
                        <input
                            data-testid="field-requested-report"
                            type="checkbox"
                            className="mt-1 accent-ash-orange w-5 h-5"
                            checked={form.requested_report}
                            onChange={setField("requested_report")}
                        />
                        <span className="text-sm text-ash-gray-dark">
                            Yes, I'd like ASH to review my results and send a more detailed Marketing Clarity Report.
                        </span>
                    </label>

                    <div className="md:col-span-2 mt-4">
                        <button type="submit" className="btn-primary" data-testid="lead-submit-btn">
                            Continue to checkup
                            <ArrowRight size={18} />
                        </button>
                    </div>
                </form>
            </section>
        </Layout>
    );
}

const Field = ({ label, required, error, children, className = "" }) => (
    <div className={className}>
        <label className="block text-xs uppercase tracking-widest font-bold text-ash-gray-dark mb-2">
            {label} {required && <span className="text-ash-orange">*</span>}
        </label>
        {children}
        {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
);
